import { useEffect, useMemo, useRef, useState } from 'react';
import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from 'd3-force';
import {
  buildLineageGraph,
  lineageCategoryFill,
  nodeRadius,
  type LineageGraph,
  type LineageNode,
} from '@/lib/lineage-graph';
import type { components } from '@trackercop/contracts';

type DetectedScript = components['schemas']['DetectedScript'];

interface LineageGraphViewProps {
  scripts: readonly DetectedScript[];
  beforeConsentOnly: boolean;
  onSelectDomain?: (node: LineageNode | null) => void;
}

interface SimNode extends SimulationNodeDatum, LineageNode {}
interface SimLink extends SimulationLinkDatum<SimNode> {
  source: string | SimNode;
  target: string | SimNode;
}

const WIDTH = 960;
const HEIGHT = 560;
const STABILISE_ALPHA = 0.01;
const HARD_CAP_MS = 3000;

// Force-directed domain-aggregated lineage view. The simulation runs on
// the main thread; it caps out after 3s or when alpha drops below 0.01,
// whichever comes first, so CPU never pegs on a large graph. Rendering
// is plain SVG — no canvas, no WebGL — which keeps the DOM inspectable
// and lets us attach `data-testid` hooks to every node. Nodes above 50
// in count are throttled: the sim strength scales inversely so large
// graphs still stabilise in the time budget.
export function LineageGraphView({
  scripts,
  beforeConsentOnly,
  onSelectDomain,
}: LineageGraphViewProps) {
  const graph = useMemo<LineageGraph>(() => buildLineageGraph(scripts), [scripts]);
  const [positions, setPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const startedAt = useRef<number>(0);

  useEffect(() => {
    if (graph.nodes.length === 0) return;

    const nodes: SimNode[] = graph.nodes.map((n) => ({ ...n }));
    const links: SimLink[] = graph.edges.map((e) => ({ source: e.source, target: e.target }));
    const strength = graph.nodes.length > 50 ? -120 : -200;

    const sim = forceSimulation(nodes)
      .force('charge', forceManyBody().strength(strength))
      .force(
        'link',
        forceLink<SimNode, SimLink>(links)
          .id((d) => d.id)
          .distance(90)
          .strength(0.4),
      )
      .force('center', forceCenter(WIDTH / 2, HEIGHT / 2))
      .force(
        'collide',
        forceCollide<SimNode>((d) => nodeRadius(d.scriptCount) + 4),
      );

    startedAt.current = Date.now();
    let frame: number | null = null;
    const tick = () => {
      sim.tick();
      const next: Record<string, { x: number; y: number }> = {};
      for (const n of nodes) {
        next[n.id] = { x: n.x ?? WIDTH / 2, y: n.y ?? HEIGHT / 2 };
      }
      setPositions(next);
      const elapsed = Date.now() - startedAt.current;
      if (sim.alpha() < STABILISE_ALPHA || elapsed > HARD_CAP_MS) {
        sim.stop();
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      sim.stop();
    };
  }, [graph]);

  const visibleNodes = beforeConsentOnly
    ? new Set(graph.nodes.filter((n) => n.hasBeforeConsent).map((n) => n.id))
    : new Set(graph.nodes.map((n) => n.id));

  function handleSelect(node: LineageNode | null) {
    setSelected(node?.id ?? null);
    onSelectDomain?.(node);
  }

  const adjacentToHover = useMemo(() => {
    if (!hovered) return new Set<string>();
    const adj = new Set<string>();
    for (const e of graph.edges) {
      if (e.source === hovered) adj.add(e.target);
      if (e.target === hovered) adj.add(e.source);
    }
    return adj;
  }, [hovered, graph.edges]);

  if (graph.nodes.length === 0) {
    return (
      <div
        className="flex h-64 items-center justify-center rounded-md border text-sm text-muted-foreground"
        data-testid="lineage-empty"
      >
        No scripts were detected on this crawl.
      </div>
    );
  }

  return (
    <svg
      role="img"
      aria-label="Script lineage graph"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="w-full max-w-full rounded-md border bg-white"
      data-testid="lineage-graph"
    >
      <g stroke="#cbd5e1" strokeWidth={1} fill="none">
        {graph.edges.map((e) => {
          const a = positions[e.source];
          const b = positions[e.target];
          if (!a || !b) return null;
          const dim = hovered && e.source !== hovered && e.target !== hovered;
          return (
            <line
              key={`${e.source}->${e.target}`}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              opacity={dim ? 0.15 : 0.6}
              data-testid={`lineage-edge-${e.source}-${e.target}`}
            />
          );
        })}
      </g>
      {graph.nodes.map((n) => {
        const pos = positions[n.id];
        if (!pos) return null;
        const visible = visibleNodes.has(n.id);
        const dim = hovered && hovered !== n.id && !adjacentToHover.has(n.id);
        const r = nodeRadius(n.scriptCount);
        return (
          <g
            key={n.id}
            transform={`translate(${pos.x},${pos.y})`}
            opacity={visible ? (dim ? 0.2 : 1) : 0.1}
            onMouseEnter={() => setHovered(n.id)}
            onMouseLeave={() => setHovered(null)}
            onClick={() => handleSelect(n)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleSelect(n);
            }}
            tabIndex={0}
            role="button"
            aria-label={`${n.domain}, ${n.scriptCount} scripts, category ${n.category}`}
            className="cursor-pointer focus:outline-none"
            data-testid={`lineage-node-${n.domain}`}
            data-selected={selected === n.id}
          >
            <circle
              r={r}
              fill={lineageCategoryFill[n.category]}
              stroke={selected === n.id ? '#0f172a' : '#ffffff'}
              strokeWidth={selected === n.id ? 3 : 1.5}
            />
            <text
              y={r + 10}
              textAnchor="middle"
              fontSize={10}
              fill="#0f172a"
              className="pointer-events-none select-none"
            >
              {n.domain.length > 28 ? `${n.domain.slice(0, 27)}…` : n.domain}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
